import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { useAuthStore } from '@/stores/authStore'
import { useEnrollmentStore } from '@/stores/enrollmentStore'
import { usePublicStore } from '@/stores/publicStore'
import { standaloneBillingRoutes } from './modules/standalone-billing'
import { standaloneBillingGuard } from './guards/standalone-billing.guard'

// Navigation guard for routes that require booking to be enabled
const bookingEnabledGuard = (to: any, from: any, next: any) => {
  const publicStore = usePublicStore()
  const preferences = publicStore.publicData?.preferences
  if (preferences && preferences.allow_booking === false) {
    next({ name: 'public-booking' })
  } else {
    next()
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'landing',
      component: () => import('../views/landing/LandingPage.vue'),
      meta: { guestOnly: true },
    },
    // Ad-campaign landing page for business owners (public, no auth redirect)
    {
      path: '/for-businesses',
      name: 'for-businesses',
      component: () => import('../views/landing/ForBusinessesPage.vue'),
      meta: { title: 'Manito for Businesses — Appointment Scheduling' },
    },
    // Authentication routes
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/auth/Login.vue'),
      meta: { guestOnly: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/auth/Register.vue'),
      meta: { guestOnly: true },
    },
    // Enrollment selection (authenticated but no enrollment selected)
    {
      path: '/enrollment-selection',
      name: 'enrollment-selection',
      component: () => import('../views/auth/EnrollmentSelection.vue'),
      meta: { requiresAuth: true },
    },
    // Project setup (authenticated but no tenant)
    {
      path: '/project-setup',
      name: 'project-setup',
      component: () => import('../views/auth/ProjectSetup.vue'),
      meta: { requiresAuth: true },
    },
    // App routes with MainLayout as parent
    {
      path: '/',
      component: () => import('../layouts/MainLayout.vue'),
      meta: { requiresAuth: true, requiresEnrollment: true },
      children: [
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('../views/app/Dashboard.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Dashboard',
            description: 'Overview of your business performance',
            icon: 'fas fa-chart-pie',
            addButtonText: 'Quick Appointment',
            addButtonAction: 'open_quick_appointment'
          }
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: () => import('../views/app/Calendar.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Calendar',
            description: 'Manage appointments and schedules',
            icon: 'fas fa-calendar-alt',
            addButtonText: 'New Appointment',
            addButtonAction: 'open_create_event'
          }
        },
        {
          path: 'services',
          name: 'services',
          component: () => import('../views/app/services/ServicesList.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Services',
            description: 'Manage your service offerings',
            icon: 'fas fa-concierge-bell',
            addButtonText: 'Add Service',
            addButtonAction: 'create_service'
          }
        },
        {
          path: 'services/create',
          name: 'service-create',
          component: () => import('../views/app/services/ServiceCreate.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Create Service',
            description: 'Add a new service to your offerings',
            icon: 'fas fa-plus-circle'
          }
        },
        {
          path: 'services/:id/edit',
          name: 'service-edit',
          component: () => import('../views/app/services/ServiceCreate.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Edit Service',
            description: 'Modify service details',
            icon: 'fas fa-edit'
          }
        },
        {
          path: 'customers',
          name: 'customers',
          component: () => import('../views/app/customers/CustomersList.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Customers',
            description: 'Manage your customer base',
            icon: 'fas fa-users',
            addButtonText: 'Add Customer',
            addButtonAction: 'create_customer'
          }
        },
        {
          path: 'customers/create',
          name: 'customer-create',
          component: () => import('../views/app/customers/CustomerCreate.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Add Customer',
            description: 'Create a new customer profile',
            icon: 'fas fa-user-plus'
          }
        },
        {
          path: 'customers/:id/edit',
          name: 'customer-edit',
          component: () => import('../views/app/customers/CustomerCreate.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Edit Customer',
            description: 'Update customer information',
            icon: 'fas fa-user-edit'
          }
        },
        {
          path: 'team',
          name: 'team',
          component: () => import('../views/app/team/StaffList.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Team',
            description: 'Manage your team members',
            icon: 'fas fa-user-tie',
            addButtonText: 'Add Team Member',
            addButtonAction: 'create_staff'
          }
        },
        {
          path: 'team/create',
          name: 'staff-create',
          component: () => import('../views/app/team/StaffCreate.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Add Team Member',
            description: 'Create a new team member profile',
            icon: 'fas fa-user-plus'
          }
        },
        {
          path: 'team/:id/edit',
          name: 'staff-edit',
          component: () => import('../views/app/team/StaffCreate.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Edit Team Member',
            description: 'Update team member information',
            icon: 'fas fa-user-edit'
          }
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('../views/app/settings/SettingsList.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Settings',
            description: 'Configure your business settings',
            icon: 'fas fa-cog'
          }
        },
        {
          path: 'settings/:settingId',
          name: 'settings-detail',
          component: () => import('../views/app/settings/SettingsList.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Settings',
            description: 'Configure your business settings',
            icon: 'fas fa-cog'
          }
        },
        {
          path: 'settings/business-configuration/edit',
          name: 'business-configuration-edit',
          component: () => import('../views/app/settings/BusinessConfigurationEdit.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Edit Business Configuration',
            description: 'Update your business information and settings',
            icon: 'fas fa-building'
          }
        },
        {
          path: 'settings/billing',
          name: 'settings-billing',
          component: () => import('../views/app/settings/SettingsList.vue'),
          meta: {
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Billing Management',
            description: 'Manage your subscription and billing settings',
            icon: 'fas fa-credit-card'
          }
        },
        {
          path: 'activity-log',
          name: 'activity-log',
          component: () => import('../views/app/ActivityLogView.vue'),
          meta: {
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Activity Log',
            description: 'View system activity and changes',
            icon: 'fas fa-history'
          }
        },
        // Billing routes - redirect old billing route to new settings billing
        {
          path: 'billing',
          redirect: '/settings/billing'
        },
        {
          path: 'checkout',
          name: 'checkout',
          component: () => import('../views/billing/CustomCheckout.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Checkout',
            description: 'Complete your subscription',
            icon: 'fas fa-shopping-cart'
          }
        },
        {
          path: 'checkout/success',
          name: 'checkout-success',
          component: () => import('../views/billing/CheckoutSuccess.vue'),
          meta: { 
            requiresAuth: true,
            requiresEnrollment: true,
            title: 'Subscription Activated',
            description: 'Your subscription has been activated',
            icon: 'fas fa-check-circle'
          }
        },
      ]
    },
    // Download app page (no tenant)
    {
      path: '/download',
      name: 'download-app',
      component: () => import('../views/public/DownloadAppView.vue'),
      meta: {
        title: 'Download Manito App',
      }
    },
    // Join redirect route (for QR code / deep link scanning)
    {
      path: '/join/:tenantSchema',
      name: 'join-redirect',
      component: () => import('../views/public/JoinRedirectView.vue'),
      meta: {
        title: 'Join Business - Manito',
      }
    },
    // Public booking routes (no auth required) - using PublicLayout
    {
      path: '/booking',
      component: () => import('../layouts/PublicLayout.vue'),
      children: [
        {
          path: '',
          name: 'public-booking',
          component: () => import('../views/public/PublicBookingView.vue'),
        },
        // Public service team members route
        {
          path: 'service/:serviceId/team',
          name: 'public-service-team',
          component: () => import('../views/public/PublicServiceTeamView.vue'),
          props: true,
          beforeEnter: bookingEnabledGuard
        },
        // Public team member services route
        {
          path: 'team/:teamMemberId/services',
          name: 'public-team-services',
          component: () => import('../views/public/PublicTeamServicesView.vue'),
          props: true,
          beforeEnter: bookingEnabledGuard
        },
        // Public calendar time slots route
        {
          path: 'calendar/:serviceId/:teamMemberId',
          name: 'public-calendar',
          component: () => import('../views/public/PublicCalendarView.vue'),
          props: true,
          beforeEnter: bookingEnabledGuard
        },
        // Public customer info route
        {
          path: 'customer-info/:serviceId/:teamMemberId',
          name: 'public-customer-info',
          component: () => import('../views/public/PublicCustomerInfoView.vue'),
          props: true,
          beforeEnter: bookingEnabledGuard
        },
        // Public profile creation route
        {
          path: 'profile-creation',
          name: 'public-profile-creation',
          component: () => import('../views/public/PublicProfileCreationView.vue'),
          beforeEnter: bookingEnabledGuard
        },
        // Public booking confirmation route
        {
          path: 'confirmation/:appointmentId',
          name: 'public-booking-confirmation',
          component: () => import('../views/public/PublicBookingConfirmationView.vue'),
          props: true,
          beforeEnter: bookingEnabledGuard
        },
        // Public appointment management route (view, cancel, reschedule)
        {
          path: 'event/:appointmentId',
          name: 'public-appointment-management',
          component: () => import('../views/public/PublicAppointmentManagementView.vue'),
          props: true
        },
        // Public user appointments list (authenticated users)
        {
          path: 'my-appointments',
          name: 'public-user-appointments',
          component: () => import('../views/public/PublicUserAppointmentsView.vue'),
        },
        // Public team member profile route (by name slug)
        {
          path: ':teamMemberSlug',
          name: 'public-team-member-profile',
          component: () => import('../views/public/PublicTeamMemberProfileView.vue'),
          props: true,
          beforeEnter: bookingEnabledGuard
        },
      ]
    },
    // Public Booking V2 (redesign)
    {
      path: '/booking2',
      name: 'public-booking-v2',
      component: () => import('../views/publicV2/PublicBookingV2View.vue'),
    },
    // Legal pages (public, no auth)
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/legal/Privacy.vue'),
      meta: { title: 'Privacy Policy' },
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/legal/Terms.vue'),
      meta: { title: 'Terms of Service' },
    },
    // Legacy routes for compatibility
    {
      path: '/home',
      name: 'home',
      component: HomeView,
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
    },
    // Standalone billing routes
    ...standaloneBillingRoutes,
  ],
})

// ─── Routes that skip auth entirely ────────────────────────────────────────
const PUBLIC_ROUTE_PREFIXES = ['/booking', '/join', '/download']
const PUBLIC_ROUTE_NAMES = new Set([
  'landing', 'for-businesses', 'privacy', 'terms', 'home', 'about',
  'public-booking-v2',
])

// ─── Auth-required routes that DON'T need enrollment ───────────────────────
const AUTH_ONLY_ROUTES = new Set([
  'enrollment-selection', 'project-setup',
])

// ─── Enhanced authentication guard ─────────────────────────────────────────
router.beforeEach(async (to, from, next) => {
  // Update document title
  const routeTitle = to.meta?.title as string
  document.title = routeTitle
    ? `${routeTitle} - Manito Appointments`
    : 'Manito Appointments'

  // Handle standalone billing routes
  if (to.path.startsWith('/standalone-billing')) {
    return standaloneBillingGuard(to, from, next)
  }

  // Skip auth for public routes
  const isPublicRoute =
    PUBLIC_ROUTE_NAMES.has(to.name as string) ||
    PUBLIC_ROUTE_PREFIXES.some(prefix => to.path.startsWith(prefix))

  if (isPublicRoute) {
    next()
    return
  }

  // ── Auth check ─────────────────────────────────────────────────────────
  const authStore = useAuthStore()
  const enrollmentStore = useEnrollmentStore()

  // Initialize auth listener if not done
  if (!authStore.isAuthReady) {
    authStore.initializeAuth()
  }

  const isAuthenticated =
    authStore.isAuthenticated ||
    localStorage.getItem('isAuthenticated') === 'true'

  // Guest-only routes (login, register, landing)
  if (to.meta.guestOnly && isAuthenticated) {
    // If authenticated, try to restore enrollment and go to dashboard
    if (!enrollmentStore.isInitialized) {
      const restored = enrollmentStore.restoreFromCache()
      if (restored) {
        next('/dashboard')
        return
      }
    }
    if (enrollmentStore.isEnrollmentReady) {
      next('/dashboard')
    } else {
      next('/enrollment-selection')
    }
    return
  }

  // Protected routes — must be authenticated
  if (to.meta.requiresAuth && !isAuthenticated) {
    next('/login')
    return
  }

  // Routes that need enrollment (dashboard, calendar, etc.)
  if (to.meta.requiresEnrollment && isAuthenticated) {
    // Try to restore from cache if not initialized
    if (!enrollmentStore.isInitialized && !enrollmentStore.isLoading) {
      enrollmentStore.restoreFromCache()
    }

    // If no enrollment selected, redirect to selection
    if (!enrollmentStore.isEnrollmentReady) {
      // Don't redirect if we're already going to enrollment-selection or project-setup
      if (!AUTH_ONLY_ROUTES.has(to.name as string)) {
        next('/enrollment-selection')
        return
      }
    }
  }

  next()
})

export default router
